import { Component } from '@theme/component';

/**
 * BNO showcase card carousel.
 *
 * A purpose-built scroll-snap carousel (NOT the stock <slideshow-component>).
 * Native horizontal scrolling gives touch swipe, momentum and reduced-motion for
 * free; CSS scroll-snap does the positioning (start snap on desktop, centre snap
 * on mobile). This class adds:
 *   - active tracking: the LEADING card of the current view gets .is-active, and
 *     data-active-index on the host is kept in sync;
 *   - .is-visible on any card at least partly in the viewport (IntersectionObserver)
 *     and .is-in-view on any card fully inside the track, which is what makes a
 *     click on it a no-op (only a partly visible card scrolls itself into place);
 *   - mouse drag-to-scroll (touch already scrolls natively);
 *   - keyboard: Left/Right arrows step between cards when the host has focus.
 *
 * The number of cards per view is breakpoint dependent (data-visible-desktop /
 * data-visible-mobile, matching the CSS 749px breakpoint), so the number of
 * reachable start positions is cards - visible + 1. Stepping is always by ONE
 * card, never by a page. The dots ship one per card (the mobile maximum) and the
 * trailing unreachable ones are hidden here, along with the whole pill when only
 * one position is reachable (2 cards on desktop) — `hidden` also takes them out
 * of the tab order. It re-runs on breakpoint change, which is the only thing
 * of the tab order. A ResizeObserver on the track re-runs all of that whenever
 * the geometry changes, so crossing the breakpoint reshapes the dots.
 *
 * No visual animation is applied to the card CONTENTS here; the card text is
 * always visible. The section-entry animation (bno-scroll.js) handles the first
 * appearance of the whole carousel when it scrolls into view.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [card]
 * @extends {Component<Refs>}
 */
class BnoShowcaseCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.track = this.refs.track;
    this.cards = this.refs.card ?? [];

    // Fewer than two cards: no carousel behaviour. Still mark the single card so
    // the animation hooks are consistent.
    if (!this.track || this.cards.length < 2) {
      this.cards[0]?.classList.add('is-active', 'is-visible');
      return;
    }

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Matches the CSS breakpoint: below it the track shows one card, above it two.
    this.mqMobile = window.matchMedia('(max-width: 749px)');
    this.visibleDesktop = parseInt(this.dataset.visibleDesktop || '2', 10) || 2;
    this.visibleMobile = parseInt(this.dataset.visibleMobile || '1', 10) || 1;
    this.activeIndex = 0;
    /** @type {number | null} */
    this.scrollRaf = null;

    // .is-visible for any card at least partly within the track's scrollport.
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.classList.toggle('is-visible', entry.isIntersecting);
        }
      },
      { root: this.track, threshold: 0.01 }
    );
    for (const card of this.cards) this.io.observe(card);

    this.dots = this.refs.dot ?? [];
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    // Clicking a peeking (partly visible) card scrolls it into place, like
    // clicking its dot. A click on a card that is already fully in view does
    // nothing, so on desktop, where both cards are always fully in view, this is
    // effectively mobile-only. A drag is NOT a click: the track's capture-phase
    // click blocker (in #setupDrag) stops the event before it reaches these
    // listeners when the pointer moved.
    this.cards.forEach((card, i) => {
      card.addEventListener('click', () => {
        if (!card.classList.contains('is-in-view')) this.goTo(i);
      });
    });

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('keydown', this.onKeydown);
    this.#setupDrag();

    // Re-run the layout whenever the track's box changes. A ResizeObserver is
    // used rather than a resize listener because it also catches the first real
    // layout after this module runs (the module can execute before the cards
    // have been sized) and any later reflow, not just window resizes. No debounce
    // is needed: the browser already batches RO callbacks to one per frame, and
    // layout() is a handful of rect reads.
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(this.track);

    this.layout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.io?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKeydown);
    this.ro?.disconnect();
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  /** Cards per view at the current breakpoint. */
  visible() {
    return this.mqMobile.matches ? this.visibleMobile : this.visibleDesktop;
  }

  /**
   * Highest reachable start index. Past it the track would show empty space, so
   * it is also the last card the browser's own scroll clamp can lead with.
   */
  maxIndex() {
    return Math.max(0, this.cards.length - this.visible());
  }

  /**
   * Re-run whenever the geometry (and so the visible count) can have changed:
   * rebuild the reachable dot set, pull the active index back in range and
   * refresh the card state.
   */
  layout() {
    this.syncControls(Math.min(this.activeIndex, this.maxIndex()));
    this.updateActive();
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateActive();
    });
  }

  /**
   * The track's content box in viewport coordinates. On desktop the track has no
   * inline padding and this is the content column; on mobile it is the full
   * width track minus the centring padding, which is exactly where a centre
   * snapped card lands. Either way, a card's resting position is `left`.
   */
  #contentBox() {
    const rect = this.track.getBoundingClientRect();
    const style = getComputedStyle(this.track);
    return {
      left: rect.left + (parseFloat(style.paddingLeft) || 0),
      right: rect.right - (parseFloat(style.paddingRight) || 0),
    };
  }

  /**
   * Active card = the leading card of the current view, i.e. the reachable start
   * position whose left edge is nearest the track's content-box left edge.
   */
  updateActive() {
    const { left, right } = this.#contentBox();
    const max = this.maxIndex();

    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i <= max; i++) {
      const dist = Math.abs(this.cards[i].getBoundingClientRect().left - left);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }

    // .is-active on the leading card; .is-before / .is-after on cards to its
    // left / right (drives the directional hover cursor). .is-in-view marks
    // every card sitting fully inside the track (sub-pixel tolerance), which is
    // what makes its click a no-op.
    this.cards.forEach((card, i) => {
      const r = card.getBoundingClientRect();
      card.classList.toggle('is-active', i === best);
      card.classList.toggle('is-before', i < best);
      card.classList.toggle('is-after', i > best);
      card.classList.toggle('is-in-view', r.left >= left - 1 && r.right <= right + 1);
    });

    if (best !== this.activeIndex || this.getAttribute('data-active-index') !== String(best)) {
      this.activeIndex = best;
      this.setAttribute('data-active-index', String(best));
      this.syncControls(best);
    }
  }

  /**
   * Reflect the active index on the pagination dots (active dot = elongated pill
   * via .is-active) and mark it as the current one for assistive tech. Also
   * trims the dot set to the positions reachable at this breakpoint: the markup
   * ships one dot per card (the mobile maximum), so on desktop the trailing ones
   * are hidden, and when only one position is reachable the whole pill goes. The
   * `hidden` attribute is what removes them from the tab order.
   * @param {number} index
   */
  syncControls(index) {
    const max = this.maxIndex();
    const vis = this.visible();

    this.dots?.forEach((dot, i) => {
      dot.hidden = i > max;
      dot.setAttribute('aria-disabled', String(i > max));
      const last = Math.min(i + vis, this.cards.length);
      dot.setAttribute('aria-label', last > i + 1 ? `Go to cards ${i + 1} to ${last}` : `Go to card ${i + 1}`);

      const active = i === index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    const wrap = this.dots?.[0]?.parentElement;
    if (wrap) wrap.hidden = max === 0;
  }

  /**
   * Scroll a card into its resting position (the track's content-box left edge,
   * which is the centre on mobile because the card fills that box there). The
   * index is clamped to the last reachable start position, so the track never
   * scrolls into empty space past the final card.
   * @param {number} index
   */
  goTo(index) {
    const i = Math.max(0, Math.min(this.maxIndex(), index));
    const card = this.cards[i];
    if (!card) return;

    const delta = card.getBoundingClientRect().left - this.#contentBox().left;
    const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
    this.track.scrollBy({ left: delta, behavior });
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.activeIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.activeIndex - 1);
    }
  }

  /** Mouse drag-to-scroll. Touch keeps native scrolling. */
  #setupDrag() {
    const track = this.track;
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let captured = false;
    let pointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Every card already fits in the track (2 cards on desktop): there is
      // nothing to scroll, so there is nothing to drag either.
      if (this.maxIndex() === 0) return;
      down = true;
      moved = false;
      captured = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      // NOTE: do NOT capture the pointer here. Capturing on pointerdown makes the
      // browser dispatch the derived `click` to the capture target (this track)
      // instead of the card under the cursor, so clicking a peeking card never
      // reaches its click handler. Capture is deferred to pointermove, once an
      // actual drag begins.
    });

    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        // A real drag has started: now suspend snapping and capture the pointer
        // so the drag keeps tracking even outside the carousel.
        moved = true;
        track.style.scrollSnapType = 'none';
        try {
          track.setPointerCapture(pointerId);
          captured = true;
        } catch (_) {
          /* ignore */
        }
      }
      if (moved) track.scrollLeft = startScroll - dx;
    });

    const end = () => {
      if (!down) return;
      down = false;
      if (moved) {
        // Restore CSS snapping -> the track snaps to the nearest card.
        track.style.scrollSnapType = '';
        if (captured) {
          try {
            track.releasePointerCapture(pointerId);
          } catch (_) {
            /* ignore */
          }
        }
        // Drag is over: re-check the active card once the snap lands.
        requestAnimationFrame(() => this.updateActive());
      }
    };

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    // A drag must not fire a click on whatever was under the cursor. Plain clicks
    // (moved === false) pass through to the card click handler.
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }
}

if (!customElements.get('bno-showcase-carousel')) {
  customElements.define('bno-showcase-carousel', BnoShowcaseCarousel);
}
